// Child lock — lock the physical keypad so only the app can switch the load.
//
// Sent as `CHILDLOCKON` / `CHILDLOCKOFF`, the same vocabulary over MQTT and BLE.
// The ESP firmware turns the word into a Tuya DP write on the serial link to the
// MCU (see docs/11-tuya-mcu-protocol.md for the frame format).
//
// ═══════════════════════════════════════════════════════════════════════════
// ⚠️ THE FIRMWARE CURRENTLY WRITES THE WRONG DATA POINT
// ═══════════════════════════════════════════════════════════════════════════
//
// The ESP builds these frames today:
//
//     ChildLockOn   55 aa 00 06 00 05 67 01 00 01 01 74
//     ChildLockOff  55 aa 00 06 00 05 67 01 00 01 00 73
//                                     ^^
//                                     DP 0x67 = 103
//
// Both checksums are CORRECT (verified: 0x74 and 0x73), which is the problem —
// a malformed frame would be rejected, a well-formed one aimed at the wrong DP
// is obeyed. Against the Tuya product definitions:
//
//     child lock is DP 40  (0x28)  — declared only on 4 SWITCH + 1 FAN
//     DP 103      is the MASTER SWITCH on 4 SWITCH + 1 FAN
//     DP 103      does not exist on 1 / 2 / 4 / 30A SWITCH
//
// So as it stands:
//   • on a 4+1  `CHILDLOCKON` writes Master Switch = 1 → TURNS THE PANEL ON
//   • on every type listed below it addresses a DP that does not exist, and the
//     MCU ignores it → the button does nothing at all
//
// The correct frames, for whoever fixes the firmware — one byte and its checksum:
//
//     ChildLockOn   55 AA 00 06 00 05 28 01 00 01 01 35
//     ChildLockOff  55 AA 00 06 00 05 28 01 00 01 00 34
//
// ⚠️ THIS IS NOT FIXABLE FROM THE APP. We send a word; the ESP picks the DP.
// Until that one byte changes, these buttons are inert on the types below. They
// are shipped now so the fix can be exercised the day it lands — and the 4+1 is
// deliberately NOT in the list, because there the same command is destructive
// rather than merely useless.
//
// ⚠️ AND THE PDF IS A PRODUCT DEFINITION, NOT PROOF OF THE MCU BUILD. It is
// possible somebody implemented 103 as child lock on purpose. One board settles
// it: send CHILDLOCKON to a 4+1 and watch whether the lights come on.

import { CMD_CHILD_LOCK_ON, CMD_CHILD_LOCK_OFF } from './commands';

/**
 * DeviceIDs that offer child lock in Settings.
 *
 * Touch panels only — a device with no keypad has nothing to lock, which is why
 * sensors, controllers and Wi-Fi-only modules are absent.
 *
 * ⚠️ THE 4+1 FAMILY (1, 92), 7+1 (93) AND 7+2 (87) ARE OMITTED ON PURPOSE.
 * They are touch panels and they belong here on merit — the 4+1 is the ONLY
 * product whose Tuya definition actually declares child lock. But DP 103 is its
 * Master Switch, so with today's firmware the button would switch the whole
 * panel on instead of locking it. Add them the moment the firmware writes 0x28.
 */
export const CHILD_LOCK_TYPES = [49, 53, 55, 56, 60];

export const supportsChildLock = (deviceId) =>
  CHILD_LOCK_TYPES.includes(Number(deviceId));

/** The command word for a lock/unlock press. */
export const childLockCommand = (lock) =>
  (lock ? CMD_CHILD_LOCK_ON : CMD_CHILD_LOCK_OFF);

export { CMD_CHILD_LOCK_ON, CMD_CHILD_LOCK_OFF };
